// src/pages/RagSearchPage.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { ragListCollections, ragAsk } from "../api/client";
import "../styles/rag.css";
import "../styles/base.css";

const API_BASE = import.meta.env.VITE_API_BASE_URL || "/api";

const MODE_HELP = {
  basic: {
    title: "Basic RAG (Fast & Predictable)",
    bullets: [
      "Uses your query as-is (no rewriting).",
      "Runs: embed → retrieve → single-pass answer.",
      "Best for: direct questions, quick lookups, stable wording.",
    ],
  },
  agentic: {
    title: "Agentic RAG (Smarter Retrieval)",
    bullets: [
      "Rewrites/expands your query to improve recall (LLM-assisted).",
      "Designed to evolve into multi-step flows later (multi-query, tools).",
      "Best for: vague questions, “summarize / analyze / compare”.",
    ],
  },
};

const SEARCH_HELP = {
  semantic: {
    title: "Semantic Search",
    desc: "Uses embeddings to find meaning-similar chunks (best default).",
  },
  keyword: {
    title: "Keyword Search",
    desc: "Finds exact term/phrase matches (best for exact wording).",
  },
  hybrid: {
    title: "Hybrid Search",
    desc: "Combines semantic + keyword for better recall & precision.",
  },
};

const DEPTH_PRESETS = {
  quick: { label: "Quick", topK: 5, mode: "basic", searchType: "semantic" },
  standard: { label: "Standard", topK: 8, mode: "agentic", searchType: "semantic" },
  deep: { label: "Deep Draft (KB-wide-ish)", topK: 20, mode: "agentic", searchType: "hybrid" },
};

function phasePct(phase) {
  if (phase === "idle") return 0;
  if (phase === "retrieving") return 35;
  if (phase === "drafting") return 80;
  if (phase === "done") return 100;
  if (phase === "error") return 0;
  return 0;
}

async function apiPostJson(path, body) {
  const token = localStorage.getItem("access_token");
  const res = await fetch(`${API_BASE}${path}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body ?? {}),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

// ------- helpers for richer tables -------
function tokenizeQuery(q) {
  return Array.from(
    new Set(
      (q || "")
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, " ")
        .split(/\s+/)
        .map((t) => t.trim())
        .filter((t) => t.length >= 3)
        .slice(0, 10)
    )
  );
}

function keywordStats(text, terms) {
  const s = (text || "").toLowerCase();
  let matchCount = 0;
  const matched = [];
  for (const t of terms) {
    if (!t) continue;
    if (s.includes(t)) {
      matched.push(t);
      // approx occurrences
      const re = new RegExp(`\\b${t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b`, "g");
      const hits = (s.match(re) || []).length;
      matchCount += hits || 1;
    }
  }
  return { matched_terms: matched, match_count: matchCount };
}

function snippet(text, n = 220) {
  const t = (text || "").replace(/\s+/g, " ").trim();
  if (t.length <= n) return t;
  return t.slice(0, n) + "…";
}

function chunkKey(c) {
  const meta = c.metadata || c.meta || {};
  return `${meta.filename || meta.file_name || "?"}::${meta.chunk_idx ?? meta.index ?? "?"}`;
}

function buildCoverage(chunks) {
  const fileMap = new Map();
  let scores = [];
  for (const c of chunks || []) {
    const meta = c.metadata || c.meta || {};
    const fn = meta.filename || meta.file_name || "Unknown";
    fileMap.set(fn, (fileMap.get(fn) || 0) + 1);

    if (typeof c.score === "number") scores.push(c.score);
  }

  const files = Array.from(fileMap.entries())
    .sort((a, b) => b[1] - a[1])
    .map(([filename, count]) => ({ filename, count }));

  const avg =
    scores.length ? scores.reduce((a, b) => a + b, 0) / scores.length : null;

  return {
    distinct_files: fileMap.size,
    top_files: files.slice(0, 10),
    min_score: scores.length ? Math.min(...scores) : null,
    max_score: scores.length ? Math.max(...scores) : null,
    avg_score: avg,
  };
}

/**
 * Very simple parser for your report-style answer.
 * Works best if answer includes headings like:
 * Executive Summary / Detailed Answer / Risks / Recommended Actions / Evidence Map
 */
function parseDraft(answer) {
  const txt = (answer || "").replace(/\r\n/g, "\n");
  const findPos = (re) => {
    const m = txt.match(re);
    return m?.index ?? -1;
  };

  const sections = [
    { key: "exec", title: "Executive Summary", re: /Executive Summary/i },
    { key: "detail", title: "Detailed Answer", re: /Detailed Answer/i },
    { key: "risks", title: "Risks / Gaps / Ambiguities", re: /Risks|Gaps|Ambiguities/i },
    { key: "actions", title: "Recommended Actions / Next Steps", re: /Recommended Actions|Next Steps/i },
    { key: "evidence", title: "Evidence Map", re: /Evidence Map/i },
  ];

  const positions = sections
    .map((s) => ({ ...s, pos: findPos(s.re) }))
    .filter((s) => s.pos >= 0)
    .sort((a, b) => a.pos - b.pos);

  // fallback: no headings
  if (!positions.length) {
    return [{ section: "Draft Answer", content: txt.trim() }];
  }

  const out = [];
  for (let i = 0; i < positions.length; i++) {
    const cur = positions[i];
    const next = positions[i + 1];
    const start = cur.pos;
    const end = next ? next.pos : txt.length;
    const slice = txt.slice(start, end).trim();
    out.push({ section: cur.title, content: slice });
  }
  return out;
}

export default function RagSearchPage() {
  const [collections, setCollections] = useState([]);
  const [collectionId, setCollectionId] = useState("");
  const [query, setQuery] = useState("");

  const [searchType, setSearchType] = useState("semantic");
  const [topK, setTopK] = useState(5);
  const [mode, setMode] = useState("agentic");
  const [depth, setDepth] = useState("standard");

  const [result, setResult] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");

  const [phase, setPhase] = useState("idle"); // idle|retrieving|drafting|done|error
  const [loading, setLoading] = useState(false);
  const [elapsedMs, setElapsedMs] = useState(0);
  const timerRef = useRef(null);

  // Retrieval tables
  const [retrSemantic, setRetrSemantic] = useState(null);
  const [retrKeyword, setRetrKeyword] = useState(null);
  const [retrCombined, setRetrCombined] = useState(null);
  const [retrTab, setRetrTab] = useState("combined"); // semantic|keyword|combined

  const [showSnippetText, setShowSnippetText] = useState(true);
  const [showChunkTextRaw, setShowChunkTextRaw] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const data = await ragListCollections();
        setCollections(data || []);
        if (data && data.length) setCollectionId(String(data[0].id));
      } catch (err) {
        console.error("Failed to load collections", err);
        setErrorMsg("Failed to load Knowledge Bases. Please try again.");
      }
    })();
  }, []);

  const selectedKb = useMemo(() => {
    return collections.find((c) => String(c.id) === String(collectionId));
  }, [collections, collectionId]);

  const startTimer = () => {
    const startedAt = Date.now();
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => setElapsedMs(Date.now() - startedAt), 200);
  };
  const stopTimer = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = null;
  };
  useEffect(() => () => stopTimer(), []);

  const applyDepthPreset = (v) => {
    setDepth(v);
    const p = DEPTH_PRESETS[v];
    if (!p) return;
    setTopK(p.topK);
    setMode(p.mode);
    setSearchType(p.searchType);
  };

  const runRetrieval = async ({ collectionIdNum, q, topKNum, st, modeVal }) => {
    // uses your FastAPI route: POST /search/collections/{collection_id}
    // NOTE: mode is optional there; we pass it to reuse agentic rewrite if present.
    return apiPostJson(`/search/collections/${collectionIdNum}`, {
      query: q,
      top_k: topKNum,
      search_type: st,
      mode: modeVal,
    });
  };

  const onAsk = async (e) => {
    e.preventDefault();
    setErrorMsg("");
    setResult(null);
    setRetrSemantic(null);
    setRetrKeyword(null);
    setRetrCombined(null);

    const q = query.trim();
    if (!collectionId) return setErrorMsg("Please select a Knowledge Base.");
    if (!q) return setErrorMsg("Please enter a query.");

    const collectionIdNum = Number(collectionId);
    const projectId = selectedKb?.project_id ?? 1;
    const topKNum = Number(topK) || 5;
    const st = searchType;
    const modeVal = mode;

    setLoading(true);
    setPhase("retrieving");
    setElapsedMs(0);
    startTimer();

    try {
      // 1) Retrieve chunks FIRST (fast)
      const terms = tokenizeQuery(q);

      if (st === "hybrid") {
        setRetrTab("combined");

        const [sem, kw] = await Promise.all([
          runRetrieval({ collectionIdNum, q, topKNum, st: "semantic", modeVal }),
          runRetrieval({ collectionIdNum, q, topKNum, st: "keyword", modeVal }),
        ]);

        // enrich keyword stats
        const semChunks = (sem?.chunks || []).map((c) => ({
          ...c,
          retrieval_source: "semantic",
          ...(terms.length ? keywordStats(c.text, terms) : {}),
        }));
        const kwChunks = (kw?.chunks || []).map((c) => ({
          ...c,
          retrieval_source: "keyword",
          ...(terms.length ? keywordStats(c.text, terms) : {}),
        }));

        // combine + dedupe by file+chunk idx
        const map = new Map();
        for (const c of [...semChunks, ...kwChunks]) {
          const k = chunkKey(c);
          if (!map.has(k)) map.set(k, c);
        }
        const combined = Array.from(map.values()).slice(0, topKNum);

        setRetrSemantic({ ...sem, chunks: semChunks });
        setRetrKeyword({ ...kw, chunks: kwChunks });
        setRetrCombined({ query: q, chunks: combined });
      } else {
        const r = await runRetrieval({ collectionIdNum, q, topKNum, st, modeVal });
        const enriched = (r?.chunks || []).map((c) => ({
          ...c,
          retrieval_source: st,
          ...(st !== "semantic" && terms.length ? keywordStats(c.text, terms) : {}),
          ...(st === "semantic" && terms.length ? keywordStats(c.text, terms) : {}), // still useful
        }));
        setRetrCombined({ ...r, chunks: enriched });
        setRetrTab("combined");
      }

      // 2) Now draft the answer
      setPhase("drafting");
      const payload = { query: q, search_type: st, top_k: topKNum, mode: modeVal };
      const res = await ragAsk(collectionIdNum, projectId, payload);
      setResult(res);

      setPhase("done");
    } catch (err) {
      console.error("RAG ask failed", err);
      setPhase("error");
      setErrorMsg(
        "Search failed. Ensure KB indexing completed and try again (check backend logs)."
      );
    } finally {
      stopTimer();
      setLoading(false);
    }
  };

  const pct = phasePct(phase);

  const coverage = useMemo(() => {
    const chunks = retrCombined?.chunks || result?.chunks || [];
    return buildCoverage(chunks);
  }, [retrCombined, result]);

  const draftTable = useMemo(() => {
    return parseDraft(result?.answer || "");
  }, [result]);

  const activeRetr = useMemo(() => {
    if (searchType !== "hybrid") return retrCombined;
    if (retrTab === "semantic") return retrSemantic;
    if (retrTab === "keyword") return retrKeyword;
    return retrCombined;
  }, [searchType, retrTab, retrSemantic, retrKeyword, retrCombined]);

  return (
    <div className="rag-page">
      <div className="rag-header">
        <h1>RAG Search</h1>
        <p className="rag-subtitle">
          Detailed Search (retrieval tables) + Drafting Result (final answer).
        </p>
      </div>

      <form className="rag-form" onSubmit={onAsk}>
        <div className="rag-grid">
          <div>
            <label>Knowledge Base</label>
            <select value={collectionId} onChange={(e) => setCollectionId(e.target.value)}>
              {collections.map((c) => (
                <option key={c.id} value={c.id}>
                  #{c.id} – {c.name}{c.project_id ? ` (Project ${c.project_id})` : ""}
                </option>
              ))}
            </select>
            {collections.length === 0 && <small>No KBs found. Create one in “RAG KB”.</small>}
          </div>

          <div>
            <label>Answer Depth</label>
            <select value={depth} onChange={(e) => applyDepthPreset(e.target.value)}>
              {Object.entries(DEPTH_PRESETS).map(([k, v]) => (
                <option key={k} value={k}>{v.label}</option>
              ))}
            </select>
            <small className="rag-help">
              Deep Draft increases retrieval breadth (higher TopK + hybrid + agentic).
            </small>
          </div>

          <div>
            <label>Search Type</label>
            <select value={searchType} onChange={(e) => setSearchType(e.target.value)}>
              <option value="semantic">Semantic</option>
              <option value="keyword">Keyword</option>
              <option value="hybrid">Hybrid</option>
            </select>
            <small className="rag-help">
              <strong>{SEARCH_HELP[searchType]?.title}:</strong> {SEARCH_HELP[searchType]?.desc}
            </small>
          </div>

          <div>
            <label>Top K</label>
            <input
              type="number"
              value={topK}
              onChange={(e) => setTopK(e.target.value)}
              min={1}
              max={50}
            />
            <small className="rag-help">Controls how many chunks are retrieved.</small>
          </div>

          <div>
            <label>Mode</label>
            <select value={mode} onChange={(e) => setMode(e.target.value)}>
              <option value="agentic">Agentic RAG</option>
              <option value="basic">Basic RAG</option>
            </select>
            <small className="rag-help">
              <strong>{MODE_HELP[mode]?.title}:</strong>
              <span className="rag-bullets">
                {MODE_HELP[mode]?.bullets?.map((b, i) => (
                  <span key={i}>• {b} </span>
                ))}
              </span>
            </small>
          </div>
        </div>

        <div>
          <label>Query</label>
          <textarea
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            rows={4}
            placeholder="Example: Summarize payment terms, milestones, acceptance criteria, termination, and risks."
          />
        </div>

        {selectedKb && (
          <div className="rag-kb-meta">
            <strong>KB:</strong> #{selectedKb.id} {selectedKb.name} ·{" "}
            <span>Project: {selectedKb.project_id ?? "-"}</span> ·{" "}
            <span>Slug: {selectedKb.slug || "-"}</span> ·{" "}
            <span>Embed: {selectedKb.embedding_model || "-"}</span>
            {" · "}
            <span>Docs: {selectedKb.doc_count ?? "-"}</span>
          </div>
        )}

        {errorMsg && <div className="rag-error">{errorMsg}</div>}

        {(loading || phase === "done" || phase === "error") && (
          <div className="rag-progress-wrapper">
            <div className="rag-progress-track">
              <div className="rag-progress-bar" style={{ width: `${pct}%` }} />
            </div>
            <div className="rag-progress-row">
              <div className="rag-progress-label">
                {phase === "retrieving" && "Retrieving chunks (detailed search results)..."}
                {phase === "drafting" && "Drafting final answer with Llama..."}
                {phase === "done" && "Completed."}
                {phase === "error" && "Error."}
              </div>
              <div className="rag-progress-time">
                {loading ? `Elapsed: ${(elapsedMs / 1000).toFixed(1)}s` : null}
              </div>
            </div>
          </div>
        )}

        <button type="submit" disabled={loading || !collections.length}>
          {loading ? "Working..." : "Run Search"}
        </button>
      </form>

      {/* =======================
          SECTION A: RUN SUMMARY
         ======================= */}
      {(retrCombined || result) && (
        <div className="rag-result">
          <h2>Run Summary</h2>
          <table className="rag-table">
            <tbody>
              <tr><th>Search Type</th><td>{searchType}</td></tr>
              <tr><th>Mode</th><td>{mode}</td></tr>
              <tr><th>Top K</th><td>{topK}</td></tr>
              <tr><th>Chunks Retrieved</th><td>{(activeRetr?.chunks || result?.chunks || []).length}</td></tr>
              <tr><th>Distinct Files Used</th><td>{coverage.distinct_files}</td></tr>
              <tr>
                <th>Score Range</th>
                <td>
                  {coverage.min_score != null ? coverage.min_score.toFixed(4) : "-"}{" "}
                  to{" "}
                  {coverage.max_score != null ? coverage.max_score.toFixed(4) : "-"}{" "}
                  (avg {coverage.avg_score != null ? coverage.avg_score.toFixed(4) : "-"})
                </td>
              </tr>
              <tr><th>Elapsed</th><td>{(elapsedMs / 1000).toFixed(1)}s</td></tr>
            </tbody>
          </table>

          <h3>KB Coverage (Top Files Referenced)</h3>
          <table className="rag-table">
            <thead>
              <tr>
                <th>File</th>
                <th># Retrieved Chunks</th>
              </tr>
            </thead>
            <tbody>
              {coverage.top_files.map((f, idx) => (
                <tr key={idx}>
                  <td style={{ maxWidth: 520 }}><small>{f.filename}</small></td>
                  <td>{f.count}</td>
                </tr>
              ))}
              {coverage.top_files.length === 0 && (
                <tr><td colSpan={2}>No file metadata available.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* =======================
          SECTION B: DETAILED SEARCH RESULTS
         ======================= */}
      {retrCombined && (
        <div className="rag-result">
          <div className="rag-result-head">
            <h2>Detailed Search Results</h2>
            <div className="rag-result-actions">
              <label className="rag-toggle">
                <input
                  type="checkbox"
                  checked={showSnippetText}
                  onChange={(e) => setShowSnippetText(e.target.checked)}
                />
                Show snippet preview
              </label>
              <label className="rag-toggle">
                <input
                  type="checkbox"
                  checked={showChunkTextRaw}
                  onChange={(e) => setShowChunkTextRaw(e.target.checked)}
                />
                Show full chunk text (raw)
              </label>
            </div>
          </div>

          {searchType === "hybrid" && (
            <div className="rag-tabs">
              <button
                type="button"
                className={retrTab === "combined" ? "active" : ""}
                onClick={() => setRetrTab("combined")}
              >
                Combined
              </button>
              <button
                type="button"
                className={retrTab === "semantic" ? "active" : ""}
                onClick={() => setRetrTab("semantic")}
              >
                Semantic hits
              </button>
              <button
                type="button"
                className={retrTab === "keyword" ? "active" : ""}
                onClick={() => setRetrTab("keyword")}
              >
                Keyword hits
              </button>
            </div>
          )}

          <table className="rag-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Score</th>
                <th>Type</th>
                <th>File</th>
                <th>Chunk</th>
                {(searchType === "keyword" || retrTab === "keyword" || searchType === "hybrid") && (
                  <>
                    <th>Match Count</th>
                    <th>Matched Terms</th>
                  </>
                )}
                {showSnippetText && <th>Snippet</th>}
                {showChunkTextRaw && <th>Chunk Text</th>}
              </tr>
            </thead>
            <tbody>
              {(activeRetr?.chunks || []).map((c, idx) => {
                const meta = c.metadata || c.meta || {};
                const filename = meta.filename || meta.file_name || "-";
                const chunkIdx = meta.chunk_idx !== undefined ? meta.chunk_idx : meta.index;

                return (
                  <tr key={idx}>
                    <td>{idx + 1}</td>
                    <td>{typeof c.score === "number" ? c.score.toFixed(4) : String(c.score ?? "-")}</td>
                    <td>{c.retrieval_source || searchType}</td>
                    <td style={{ maxWidth: 280 }}><small>{filename}</small></td>
                    <td>{chunkIdx ?? "-"}</td>

                    {(searchType === "keyword" || retrTab === "keyword" || searchType === "hybrid") && (
                      <>
                        <td>{c.match_count ?? "-"}</td>
                        <td style={{ maxWidth: 220 }}>
                          <small>{Array.isArray(c.matched_terms) ? c.matched_terms.join(", ") : "-"}</small>
                        </td>
                      </>
                    )}

                    {showSnippetText && (
                      <td style={{ maxWidth: 520 }}>
                        <small>{snippet(c.text)}</small>
                      </td>
                    )}

                    {showChunkTextRaw && (
                      <td style={{ maxWidth: 520 }}>
                        <small>{c.text}</small>
                      </td>
                    )}
                  </tr>
                );
              })}
              {(activeRetr?.chunks || []).length === 0 && (
                <tr><td colSpan={showChunkTextRaw ? 10 : 9}>No chunks found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* =======================
          SECTION C: DRAFTING RESULT
         ======================= */}
      {result && (
        <div className="rag-result">
          <h2>Drafting Result (Final Answer)</h2>

          <h3>Draft Summary Table</h3>
          <table className="rag-table">
            <thead>
              <tr>
                <th>Section</th>
                <th>Draft</th>
              </tr>
            </thead>
            <tbody>
              {draftTable.map((row, idx) => (
                <tr key={idx}>
                  <td style={{ width: 220 }}><strong>{row.section}</strong></td>
                  <td>
                    <pre className="rag-answer-pre" style={{ margin: 0 }}>
                      {row.content}
                    </pre>
                  </td>
                </tr>
              ))}
              {draftTable.length === 0 && (
                <tr><td colSpan={2}>No draft available.</td></tr>
              )}
            </tbody>
          </table>

          <h3>Full Answer</h3>
          <div className="rag-answer-box">
            <pre className="rag-answer-pre">{result.answer}</pre>
          </div>
        </div>
      )}
    </div>
  );
}
